import { useEffect, type ReactNode } from 'react';
import { dispatchKey, keyFromEvent, pushKeyLayer, type NavKey } from '@/lib/keys.ts';
import { nearestInDirection, type Direction } from './spatial.ts';
import { restorePending } from '@/lib/focus-memory.ts';

/**
 * Spatial navigation.
 *
 * THREE RULES, each of which cost an evening to learn.
 *
 * 1. THE DOM OWNS FOCUS, and every element is tabIndex={0}.
 *
 *    Android's WebView does its OWN D-pad navigation, moving focus between
 *    tabbable elements before JS ever sees the key. A roving tabindex leaves
 *    exactly one tabbable element on the page, so the WebView has nowhere to
 *    move, swallows the arrow keys, and the remote appears completely dead —
 *    while Enter still works, because that path is different. Desktop browsers
 *    hid the problem: they do no spatial navigation at all.
 *
 * 2. THE PROVIDER MUST NOT RE-RENDER WHEN FOCUS MOVES.
 *
 *    It used to hold the focused id in state and hand a fresh object literal
 *    to a context on every render, so one D-pad press re-rendered every
 *    Focusable in the tree — sixty cards on a Fire TV Stick — and re-ran each
 *    one's effect, whose autoFocus branch then dragged focus back to the first
 *    card. The remote felt like it was fighting you because it was. There is
 *    now no state and no context here at all, so a keypress costs a style
 *    recalc and zero renders.
 *
 * 3. THE CANDIDATE SET COMES FROM THE DOM, NOT A REGISTRY.
 *
 *    This is the one that bit last. A registry only ever contains what someone
 *    remembered to route through <Focusable>, so the nav links — ordinary
 *    <NavLink> anchors — were invisible to the remote. "Live TV" could not be
 *    reached at all, and nothing anywhere reported a problem, because from the
 *    registry's point of view nothing was wrong. A parallel list of what is
 *    focusable will always drift from what IS focusable.
 *
 *    So: anything carrying `data-nav` participates, whatever rendered it, and
 *    `navProps()` stamps that attribute for you. Adding a navigable
 *    element can no longer half-work.
 */

const NAV_SELECTOR = '[data-nav]';
const SCOPE_SELECTOR = '[data-nav-scope]';

/**
 * Props every navigable element needs. Spread them onto anything.
 *
 * Deliberately NOT named `useFocusable`: it calls no React hooks, and it gets
 * spread inside conditional JSX. A `use` prefix on something that is legal to
 * call conditionally is a trap for whoever adds a real hook to it later.
 */
export function navProps(id: string) {
  return { 'data-nav': id, tabIndex: 0 } as const;
}

/**
 * Everything the remote can land on, in document order.
 *
 * Filtered by REAL visibility, not just by size: the channel grid keeps its
 * layout while theater hides it, so a rect test alone would happily move focus
 * into sixty invisible cards behind the video. `visibility` inherits, so
 * asking the element itself covers the whole hidden subtree.
 */
function candidates(): HTMLElement[] {
  return [...navRoot().querySelectorAll<HTMLElement>(NAV_SELECTOR)].filter((el) => {
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return false;
    return getComputedStyle(el).visibility === 'visible';
  });
}

/**
 * Where the search starts: the topmost open modal, or the whole document.
 *
 * A dialog does not hide what is behind it — it is a scrim, and the grid
 * underneath stays laid out and visible — so without this, one press of DOWN
 * inside a three-item menu walks out of it and lands on a channel card the
 * user cannot see is focused. `[data-nav-scope]` (see ui/Modal.tsx) confines
 * the candidate set to the dialog for as long as it is open.
 *
 * Last one wins, because nested dialogs render in order and the later one is
 * the one on top. Invisible scopes are skipped, so a dialog that is mounted
 * but hidden cannot trap the remote in itself — the failure mode of a trap is
 * a dead remote, which is worse than the bug it fixes.
 */
function navRoot(): Document | HTMLElement {
  return openScope() ?? document;
}

/**
 * Is a modal open?
 *
 * Exported because a key layer that acts while a dialog is up is acting on
 * the screen behind it, and the person can neither see nor undo what it did.
 * Anything global enough to claim the arrow keys has to ask.
 */
export function isModalOpen(): boolean {
  return openScope() !== null;
}

/** The topmost open modal, if any. */
function openScope(): HTMLElement | null {
  const scopes = [...document.querySelectorAll<HTMLElement>(SCOPE_SELECTOR)]
    .filter((el) => getComputedStyle(el).visibility === 'visible');
  return scopes.length ? scopes[scopes.length - 1] : null;
}

/**
 * The scrolling surface an element rides on.
 *
 * Vertical only: horizontal strips are a different question, and treating a
 * poster row as its own surface would stop UP ever leaving it. `document` is
 * the answer for anything pinned or in normal flow, which is what makes the
 * navigation bar and a scrolled grid distinguishable.
 */
function scrollParent(el: HTMLElement): Element | Document {
  let node: HTMLElement | null = el.parentElement;
  while (node) {
    const style = getComputedStyle(node);
    const oy = style.overflowY;
    if ((oy === 'auto' || oy === 'scroll') && node.scrollHeight > node.clientHeight + 1) {
      return node;
    }
    node = node.parentElement;
  }
  return document;
}

/**
 * Which band of the shell an element lives in: 0 top chrome, 1 content, 2
 * bottom chrome.
 *
 * Read from the nearest ancestor carrying `data-nav-zone`, so a whole bar is
 * labelled once rather than every button inside it. Unlabelled means content,
 * which is why only the chrome had to say anything.
 *
 * This exists because the scroll-surface rule cannot tell the top nav from
 * the bottom player — both sit outside the scrolling area, so both "share a
 * surface" with each other and neither shares one with the content between
 * them. Pressing DOWN from the library tabs went to the now-playing bar,
 * crossing the entire page to get there. See spatial.ts.
 */
function zoneOf(el: HTMLElement): number {
  const labelled = el.closest<HTMLElement>('[data-nav-zone]');
  const value = labelled?.dataset.navZone;
  return value === 'top' ? 0 : value === 'bottom' ? 2 : 1;
}

/*
 * WHERE THE VERTICAL JOURNEY STARTED.
 *
 * Held here rather than inside spatial.ts because it is a property of a
 * SEQUENCE of presses, not of one calculation — and this is the only place
 * that sees the sequence.
 *
 * Cleared whenever focus arrives by any route other than a vertical press: a
 * horizontal move, a click, a restored cursor, a dialog opening. All of those
 * mean the person has chosen a new column, and carrying the old one forward
 * would be the same drift in a different direction.
 */
let anchorX: number | undefined;
let anchorFrom: Element | null = null;

export function forgetColumn(): void {
  anchorX = undefined;
  anchorFrom = null;
}

/** Move focus one step. Shared by the browser keys and the Android bridge. */
function move(dir: NavKey): boolean {
  if (dir !== 'up' && dir !== 'down' && dir !== 'left' && dir !== 'right') return false;
  const all = candidates();
  if (all.length === 0) return false;

  const active = document.activeElement as HTMLElement | null;

  // Deliberately NOT `all.includes(active)`. When the player chrome fades out
  // while focus is sitting on one of its buttons, that button stops being a
  // landing spot but must still be a starting point — otherwise the first
  // press of the remote teleports you to the top-left card.
  // `root.contains` as well as the selector: when a dialog opens, focus is
  // moved into it, but a stray focus still sitting on the grid behind must
  // count as "nothing focused" so the first press lands inside the dialog
  // rather than trying to navigate from a card that is no longer reachable.
  const root = navRoot();
  const current = active?.matches?.(NAV_SELECTOR) && root.contains(active) ? active : null;

  // Nothing focused yet (fresh page, or focus is on <body>): take the first
  // element rather than doing nothing, so the remote always works.
  if (!current) { all[0].focus(); return true; }

  // One measurement pass. getBoundingClientRect forces layout, and asking for
  // it inside the scoring loop is how sixty cards on a Fire TV Stick turn one
  // press of the remote into sixty reflows.
  const mine = scrollParent(current);

  /*
   * A vertical move continues the journey only if it starts from where the
   * last one landed. Anything else — a click, a restore, a sideways step —
   * means the column is new.
   */
  const vertical = dir === 'up' || dir === 'down';
  const continuing = vertical && anchorFrom === current;
  // A new journey scores from the element itself; only a continuing one
  // carries the remembered column.
  if (!continuing) anchorX = undefined;

  const i = nearestInDirection(
    current.getBoundingClientRect(),
    all.map((el) => el.getBoundingClientRect()),
    dir as Direction,
    all.indexOf(current),
    // Whether each candidate scrolls with us. Rects are viewport coordinates,
    // so anything pinned stays put while content slides away — and past a
    // certain scroll depth the pinned thing measures NEARER than the row you
    // actually meant. See spatial.ts.
    all.map((el) => scrollParent(el) === mine),
    all.map(zoneOf),
    anchorX,
  );
  if (i < 0) return false;
  all[i].focus();

  /*
   * ANCHOR ON WHERE WE LANDED, not on where we came from — and that matters
   * for exactly one case, which is the case that started all this.
   *
   * Coming down out of the chrome enters the grid at its LEFT (see
   * spatial.ts), deliberately ignoring which word you were standing on. If
   * the journey's column were then taken from that word, the next press down
   * would score against a nav link's centre and drift right again — the bug
   * fixed one press later. Taking it from the tile we actually landed on
   * means the column is the one now under the cursor, which is the one the
   * person can see.
   */
  if (vertical) {
    if (!continuing) {
      const box = all[i].getBoundingClientRect();
      anchorX = (box.left + box.right) / 2;
    }
    anchorFrom = all[i];
  } else {
    anchorX = undefined;
    anchorFrom = null;
  }
  return true;
}

/** Activate whatever is focused. */
function select(): boolean {
  const active = document.activeElement as HTMLElement | null;
  if (active && typeof active.click === 'function') { active.click(); return true; }
  return false;
}

/** A text field wants the arrow keys for its cursor. Leave it alone. */
function isTextEntry(el: Element | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable;
}

/**
 * Spatial navigation as the BASE layer of the key pipeline, plus the two
 * entry points that feed it. Renders nothing and holds no state, so it can
 * sit at the root of the app without ever re-rendering it.
 */
export function FocusProvider({ children }: { children: ReactNode }) {
  // Bottom of the stack: whatever no other layer wanted lands here.
  useEffect(() => pushKeyLayer((key: NavKey) => {
    if (isTextEntry(document.activeElement)) return false;
    if (key === 'select') return select();
    if (key === 'back') return false;      // presentation's business, not ours
    if (key === 'menu') return false;      // nobody wanted it; let the wrapper have it

    const moved = move(key);
    if (moved) return true;

    /*
     * A MODAL THAT LEAKS IS NOT A MODAL.
     *
     * Returning false here means "nobody handled this", and the Android
     * wrapper answers that by letting the WebView do its OWN D-pad
     * navigation — which knows nothing about dialogs and walks focus to the
     * next tabbable element in document order. From a dialog, that is
     * whatever is behind it.
     *
     * That is what "it falls to the hidden menu behind it" was: not our
     * navigation going wrong, but ours declining and the platform's taking
     * over. So while a scope is open, an unproductive key is CONSUMED. The
     * cost is a keypress that does nothing at the edge of a dialog, which is
     * what a dialog edge should do anyway.
     */
    return openScope() !== null;
  }, { base: true }), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const key = keyFromEvent(e);
      if (!key) return;
      // preventDefault stops the WebView ALSO moving focus or scrolling.
      if (dispatchKey(key)) e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /**
   * The bridge the Android wrapper drives.
   *
   * Android's WebView eats D-pad arrows as a scroll gesture, so they never
   * arrive as `keydown` in the page. The APK therefore intercepts them in
   * dispatchKeyEvent and calls these directly. In a browser nothing calls
   * them and the keydown handler above does the work — one pipeline, two
   * entry points.
   *
   * __n8mBack returns whether the PAGE handled it. The wrapper needs that
   * answer: BACK that the page consumed must not also pop the WebView's
   * history or close the app, which is precisely what "back takes you out of
   * the app entirely" was.
   */
  useEffect(() => {
    const w = window as any;
    w.__n8mNav = (dir: NavKey) => dispatchKey(dir);
    w.__n8mSelect = () => dispatchKey('select');
    w.__n8mBack = () => dispatchKey('back');
    w.__n8mMenu = () => dispatchKey('menu');
    return () => {
      delete w.__n8mNav; delete w.__n8mSelect; delete w.__n8mBack; delete w.__n8mMenu;
    };
  }, []);

  return <>{children}</>;
}

export function Focusable({
  id, onSelect, children, className, autoFocus, backdrop, 'aria-label': ariaLabel,
}: {
  id: string;
  onSelect?: () => void;
  children: ReactNode;
  className?: string;
  autoFocus?: boolean;
  /** The image to put behind the app while this holds the cursor. */
  backdrop?: string | null;
  /**
   * For when the visible content stops being the accessible name — the
   * mobile bottom bar replaces a button's text with an icon under a narrow
   * viewport (see styles.css), and a screen reader needs the word that is no
   * longer on screen. Optional: most callers still say what they do in
   * plain, always-visible text, and do not need this at all.
   */
  'aria-label'?: string;
}) {
  const nav = navProps(id);

  useEffect(() => {
    // Once, and only into a page that has no focus yet. Stealing focus on a
    // remount — a route change, a grid refetch — yanks the user out of
    // wherever they already are, mid-navigation.
    if (!autoFocus) return;
    /*
     * And not at all while the cursor is being put back where it was. This
     * element is the FIRST one on the screen; the restore is heading for the
     * four-hundredth. Both running means landing at the top and then jumping,
     * which reads as a twitch rather than as a restore.
     */
    if (restorePending()) return;
    /*
     * "The page has no cursor" means NOTHING NAVIGABLE holds focus — not
     * merely that <body> does.
     *
     * The old test named <body> specifically, and a WebView does not always
     * oblige: on the television focus frequently sits on <html>, or on a
     * container that is not a landing spot, and the guard then declined. The
     * page came up with no cursor at all, so the first press of the remote
     * was spent claiming one instead of moving — the "you have to press it
     * twice" that survived the focus-memory change, because it was never the
     * same bug.
     */
    const active = document.activeElement as HTMLElement | null;
    const hasCursor = !!active?.matches?.('[data-nav]');
    if (!hasCursor) {
      document.querySelector<HTMLElement>(`[data-nav="${CSS.escape(id)}"]`)?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * ONE ACTIVATION PATH, and this used to be a second one.
   *
   * Enter already maps to `select` in the key layer (keyFromEvent), and
   * `select()` activates by calling `.click()` on whatever holds focus —
   * which reaches the onClick below. This element ALSO handled Enter itself,
   * so on a television, where the native wrapper dispatches select and the
   * WebView may deliver a real keydown as well, one press of OK could fire
   * twice.
   *
   * The symptom was specific and looked like something else entirely:
   * pressing OK on a film navigated to its detail screen AND started playing
   * it, because the first activation navigated, `autoFocus` put the cursor on
   * Play, and the second activation pressed it. Any screen whose first
   * control autofocuses was one press away from the same thing.
   *
   * Space is not handled anywhere now, which is correct: there is no space
   * bar on a remote, and in a browser Enter is the convention.
   */

  return (
    <div
      {...nav}
      {...(backdrop ? { 'data-backdrop': backdrop } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
      className={className}
      // No focused-state here. The focus RING is CSS :focus; React renders
      // nothing at all when the remote moves, which is the whole point.
      onClick={() => onSelect?.()}
      role="button"
    >
      {children}
    </div>
  );
}
